import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  getDocs,
  query,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { db } from "../../../firebaseConfig";

import {
  matchOfficeDesign,
  getFaceSuggestion,
} from "../../../utils/officeScheme/matchOfficeDesign";

import { OFFICE_STATUS } from "../../../utils/officeScheme/officeSchemeStatus";

import {
  calculateOfficeSchemeFee,
  formatINR,
} from "../../../utils/officeScheme/gstCalculator";

import { generateOfficeProposalPDF } from "../../../utils/officeScheme/generateOfficeProposalPDF";

const STATE_OPTIONS = ["Gujarat"];

const DISTRICT_OPTIONS = [
  "Banaskantha",
  "Patan",
  "Mehsana",
  "Sabarkantha",
  "Aravalli",
  "Gandhinagar",
  "Ahmedabad",
  "Kutch",
  "Rajkot",
  "Surat",
  "Vadodara",
  "Bhavnagar",
];

const TALUKA_OPTIONS = {
  Banaskantha: [
    "Palanpur",
    "Dantiwada",
    "Deesa",
    "Dhanera",
    "Tharad",
    "Vav",
    "Vadgam",
    "Amirgadh",
    "Kankrej",
  ],
  Patan: ["Patan", "Sidhpur", "Radhanpur", "Chanasma", "Harij", "Sami"],
  Mehsana: ["Mehsana", "Kadi", "Visnagar", "Vijapur", "Unjha", "Becharaji"],
  Sabarkantha: ["Himatnagar", "Idar", "Prantij", "Talod", "Khedbrahma"],
  Aravalli: ["Modasa", "Bhiloda", "Malpur", "Meghraj", "Dhansura"],
  Gandhinagar: ["Gandhinagar", "Kalol", "Mansa", "Dehgam"],
  Ahmedabad: ["Ahmedabad", "Daskroi", "Sanand", "Dholka", "Bavla"],
  Kutch: ["Bhuj", "Anjar", "Gandhidham", "Mandvi", "Mundra"],
  Rajkot: ["Rajkot", "Gondal", "Jetpur", "Dhoraji", "Upleta"],
  Surat: ["Surat", "Bardoli", "Olpad", "Kamrej", "Mangrol"],
  Vadodara: ["Vadodara", "Padra", "Dabhoi", "Savli", "Karjan"],
  Bhavnagar: ["Bhavnagar", "Ghogha", "Talaja", "Mahuva", "Palitana"],
};

const SERVICES_INCLUDED = [
  "Architectural office design as per selected model",
  "District-wise estimate document selection",
  "PACS/Society name overlay on estimate document",
  "R&B verification coordination support",
  "Engineer stage-wise completion certificate support",
  "CA utilization certificate coordination support",
  "District Registrar / subsidy documentation support",
  "Online tracking dashboard and document record",
];

export default function OfficeSchemeApplyForm() {
  const [form, setForm] = useState({
    societyName: "",
    registrationNo: "",
    panNo: "",
    gstNo: "",
    chairmanName: "",
    secretaryName: "",
    address: "",
    state: "Gujarat",
    district: "",
    taluka: "",
    village: "",
    email: "",
    mobile: "",
    east: "",
    west: "",
    north: "",
    south: "",
    frontSide: "",
  });

  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [matchedEstimate, setMatchedEstimate] = useState(null);
  const [applicationId, setApplicationId] = useState("");

  const designResult = useMemo(() => {
    return matchOfficeDesign({
      east: form.east,
      west: form.west,
      north: form.north,
      south: form.south,
    });
  }, [form.east, form.west, form.north, form.south]);

  const fee = useMemo(() => {
    return calculateOfficeSchemeFee(matchedEstimate?.estimateAmount || 0);
  }, [matchedEstimate]);

  const talukas = useMemo(() => {
    return TALUKA_OPTIONS[form.district] || [];
  }, [form.district]);

  useEffect(() => {
    setMatchedEstimate(null);
  }, [
    form.district,
    form.east,
    form.west,
    form.north,
    form.south,
    form.frontSide,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "district") {
        next.taluka = "";
        next.village = "";
      }

      return next;
    });
  };

  const validateBasic = () => {
    if (!form.societyName.trim()) {
      alert("Society Name required hai.");
      return false;
    }

    if (!form.registrationNo.trim()) {
      alert("Registration Number required hai.");
      return false;
    }

    if (!form.district) {
      alert("District select karo.");
      return false;
    }

    if (!form.taluka) {
      alert("Taluka select karo.");
      return false;
    }

    if (!form.village.trim()) {
      alert("Village required hai.");
      return false;
    }

    if (!form.mobile.trim()) {
      alert("Mobile Number required hai.");
      return false;
    }

    if (!form.east || !form.west || !form.north || !form.south) {
      alert("Plot ke East, West, North, South measurement required hai.");
      return false;
    }

    if (!form.frontSide) {
      alert("Office face / front side select karo.");
      return false;
    }

    return true;
  };

  const findEstimate = async () => {
    if (!validateBasic()) return null;

    try {
      setChecking(true);

      const q = query(
        collection(db, "district_estimate_master"),
        where("districtName", "==", form.district),
        where("designId", "==", designResult.selectedDesign.id),
        where("status", "==", "ACTIVE")
      );

      const snap = await getDocs(q);

      if (snap.empty) {
        alert(
          `Master data missing:\n\nDistrict: ${form.district}\nDesign: ${designResult.selectedDesign.id}\n\nDistrict Estimate Master me ye record upload karo.`
        );
        return null;
      }

      const docSnap = snap.docs[0];
      const data = { id: docSnap.id, ...docSnap.data() };

      setMatchedEstimate(data);
      return data;
    } catch (err) {
      console.error("Estimate check error:", err);
      alert(`Estimate check error: ${err.message}`);
      return null;
    } finally {
      setChecking(false);
    }
  };

  const downloadProposal = async () => {
    const estimate = matchedEstimate || (await findEstimate());
    if (!estimate) return;

    generateOfficeProposalPDF({
      form,
      designResult,
      estimate,
    });
  };

  const submitApplication = async (e) => {
    e.preventDefault();

    const estimate = matchedEstimate || (await findEstimate());
    if (!estimate) return;

    try {
      setSaving(true);

      const calculatedFee = calculateOfficeSchemeFee(estimate.estimateAmount);

      const docRef = await addDoc(collection(db, "office_scheme_applications"), {
        ...form,

        plotArea: designResult.plotArea,
        avgWidth: designResult.avgWidth,
        avgLength: designResult.avgLength,
        selectedDesign: designResult.selectedDesign,

        estimateMasterId: estimate.id,
        estimateAmount: Number(estimate.estimateAmount) || 0,
        subsidyAmount: Number(estimate.subsidyAmount) || 0,
        societyShare: Number(estimate.societyShare) || 0,

        estimatePdfUrl: estimate.estimatePdfUrl || "",
        designPdfUrl: estimate.designPdfUrl || "",
        rnbVerifiedPdfUrl: estimate.rnbVerifiedPdfUrl || "",

        consultancyFee: calculatedFee.consultancyFee,
        cgst: calculatedFee.cgst,
        sgst: calculatedFee.sgst,
        totalGst: calculatedFee.totalGst,
        invoiceTotal: calculatedFee.grandTotal,

        paymentStatus: "PENDING",
        status: OFFICE_STATUS.APPLIED,

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setApplicationId(docRef.id);

      alert(
        `Application submit ho gayi.\n\nApplication ID: ${docRef.id}\n\nAb Proposal PDF download kar sakte ho.`
      );
    } catch (err) {
      console.error("Application submit error:", err);
      alert(`Application submit error: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };
    return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="bg-gradient-to-r from-emerald-700 to-blue-900 rounded-3xl shadow-xl p-6 text-white mb-6">
          <h1 className="text-3xl font-bold">
            PACS Office Design & Estimate Application
          </h1>
          <p className="text-sm opacity-90 mt-1">
            Auto design selection, district-wise estimate matching, proposal PDF
            and payment calculation.
          </p>
        </div>

        <form
          onSubmit={submitApplication}
          className="grid grid-cols-1 xl:grid-cols-3 gap-6"
        >
          <div className="xl:col-span-2 space-y-6">
            <Section title="1. Society / PACS Details">
              <Input
                label="Society / PACS Name"
                name="societyName"
                value={form.societyName}
                onChange={handleChange}
                required
              />

              <Input
                label="Registration Number"
                name="registrationNo"
                value={form.registrationNo}
                onChange={handleChange}
                required
              />

              <Input
                label="PAN Number"
                name="panNo"
                value={form.panNo}
                onChange={handleChange}
              />

              <Input
                label="GST Number Optional"
                name="gstNo"
                value={form.gstNo}
                onChange={handleChange}
              />

              <Input
                label="Chairman Name"
                name="chairmanName"
                value={form.chairmanName}
                onChange={handleChange}
              />

              <Input
                label="Secretary Name"
                name="secretaryName"
                value={form.secretaryName}
                onChange={handleChange}
              />

              <Input
                label="Mobile Number"
                name="mobile"
                value={form.mobile}
                onChange={handleChange}
                required
              />

              <Input
                label="Email ID"
                name="email"
                value={form.email}
                onChange={handleChange}
                type="email"
              />
            </Section>

            <Section title="2. Address Details">
              <Select
                label="State"
                name="state"
                value={form.state}
                onChange={handleChange}
                options={STATE_OPTIONS}
              />

              <Select
                label="District"
                name="district"
                value={form.district}
                onChange={handleChange}
                options={DISTRICT_OPTIONS}
                placeholder="Select District"
              />

              <Select
                label="Taluka"
                name="taluka"
                value={form.taluka}
                onChange={handleChange}
                options={talukas}
                placeholder="Select Taluka"
              />

              <Input
                label="Village"
                name="village"
                value={form.village}
                onChange={handleChange}
                required
              />

              <div className="md:col-span-2">
                <Input
                  label="Full Address"
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                />
              </div>
            </Section>

            <Section title="3. Plot Measurement & Office Face">
              <Input
                label="East Side ft"
                name="east"
                value={form.east}
                onChange={handleChange}
                type="number"
                required
              />

              <Input
                label="West Side ft"
                name="west"
                value={form.west}
                onChange={handleChange}
                type="number"
                required
              />

              <Input
                label="North Side ft"
                name="north"
                value={form.north}
                onChange={handleChange}
                type="number"
                required
              />

              <Input
                label="South Side ft"
                name="south"
                value={form.south}
                onChange={handleChange}
                type="number"
                required
              />

              <div className="md:col-span-2">
                <label className="text-sm font-bold text-slate-700">
                  Office Face / Front Side
                </label>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                  {["East", "West", "North", "South"].map((side) => (
                    <button
                      key={side}
                      type="button"
                      onClick={() =>
                        setForm((prev) => ({ ...prev, frontSide: side }))
                      }
                      className={`py-3 rounded-xl border font-bold ${
                        form.frontSide === side
                          ? "bg-emerald-600 text-white border-emerald-600"
                          : "bg-white text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      {side}
                    </button>
                  ))}
                </div>
              </div>
            </Section>

            <Section title="4. Services Included">
              <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                {SERVICES_INCLUDED.map((service, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-2 bg-emerald-50 border border-emerald-100 rounded-xl p-3"
                  >
                    <span className="font-bold text-emerald-700">✓</span>
                    <span className="text-sm text-slate-700">{service}</span>
                  </div>
                ))}
              </div>
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={findEstimate}
                disabled={checking}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-2xl shadow"
              >
                {checking ? "Checking..." : "Check Estimate"}
              </button>

              <button
                type="button"
                onClick={downloadProposal}
                className="bg-purple-700 hover:bg-purple-800 text-white font-bold py-4 rounded-2xl shadow"
              >
                Download Proposal PDF
              </button>

              <button
                type="submit"
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-4 rounded-2xl shadow"
              >
                {saving ? "Submitting..." : "Submit Application"}
              </button>
            </div>
          </div>

          <div className="space-y-5">
            <SummaryCard title="Auto Design Selection">
              <Info label="Plot Area" value={`${designResult.plotArea} Sq.ft`} />
              <Info label="Average Width" value={`${designResult.avgWidth} ft`} />
              <Info label="Average Length" value={`${designResult.avgLength} ft`} />
              <Info
                label="Selected Design"
                value={designResult.selectedDesign?.title}
              />
              <Info
                label="Design Size"
                value={designResult.selectedDesign?.sizeLabel}
              />
              <div className="mt-3 bg-blue-50 border border-blue-100 rounded-xl p-3 text-sm text-blue-800 font-semibold">
                {getFaceSuggestion(form.frontSide)}
              </div>
            </SummaryCard>

            <SummaryCard title="District Estimate Match">
              {matchedEstimate ? (
                <>
                  <Info
                    label="Estimate Amount"
                    value={formatINR(matchedEstimate.estimateAmount)}
                  />
                  <Info
                    label="Subsidy Amount"
                    value={formatINR(matchedEstimate.subsidyAmount)}
                  />
                  <Info
                    label="Society Share"
                    value={formatINR(matchedEstimate.societyShare)}
                  />
                  <Info
                    label="Estimate PDF"
                    value={matchedEstimate.estimatePdfUrl ? "Uploaded" : "-"}
                  />
                  <Info
                    label="Design PDF"
                    value={matchedEstimate.designPdfUrl ? "Uploaded" : "-"}
                  />
                  <Info
                    label="R&B PDF"
                    value={matchedEstimate.rnbVerifiedPdfUrl ? "Uploaded" : "-"}
                  />
                </>
              ) : (
                <p className="text-sm text-slate-500">
                  District, plot measurement and face side select karke
                  <b> Check Estimate</b> dabao.
                </p>
              )}
            </SummaryCard>

            <SummaryCard title="Payment Calculation">
              <Info label="Estimate Cost" value={formatINR(fee.totalEstimate)} />
              <Info
                label="Consultancy Fee 1.5%"
                value={formatINR(fee.consultancyFee)}
              />
              <Info label="CGST 9%" value={formatINR(fee.cgst)} />
              <Info label="SGST 9%" value={formatINR(fee.sgst)} />

              <div className="mt-4 rounded-2xl bg-slate-900 text-white p-4">
                <p className="text-xs opacity-80">Total Payable</p>
                <h2 className="text-2xl font-bold">
                  {formatINR(fee.grandTotal)}
                </h2>
              </div>
            </SummaryCard>

            <SummaryCard title="Bank Payment Terms">
              <Info label="Bank" value="INDUSIND BANK" />
              <Info label="A/C No" value="201035786608" />
              <Info label="IFSC" value="INDB0002348" />
              <p className="text-xs text-slate-500 mt-3">
                Payment RTGS/NEFT/Bank Transfer se hi accepted rahega. Payment
                verification ke baad invoice, receipt aur final documents release
                honge.
              </p>
            </SummaryCard>

            {applicationId && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">
                <p className="text-sm text-emerald-700 font-semibold">
                  Application Submitted
                </p>
                <p className="text-xs mt-1 break-all">{applicationId}</p>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-3xl shadow p-6">
      <h2 className="text-xl font-bold text-slate-800 mb-5">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Input({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
}) {
  return (
    <div>
      <label className="text-sm font-bold text-slate-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full mt-1 border rounded-xl px-3 py-3 focus:ring-2 focus:ring-emerald-500 outline-none"
      />
    </div>
  );
}

function Select({
  label,
  name,
  value,
  onChange,
  options,
  placeholder = "Select",
}) {
  return (
    <div>
      <label className="text-sm font-bold text-slate-700">{label}</label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full mt-1 border rounded-xl px-3 py-3 focus:ring-2 focus:ring-emerald-500 outline-none bg-white"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function SummaryCard({ title, children }) {
  return (
    <div className="bg-white rounded-3xl shadow p-5">
      <h3 className="font-bold text-slate-800 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="flex justify-between gap-3 border-b py-2 text-sm">
      <span className="text-slate-500">{label}</span>
      <b className="text-right">{value || "-"}</b>
    </div>
  );
}